'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { StreamVideo, StreamVideoClient, type User } from '@stream-io/video-react-sdk';
import { STREAM_TOKEN_ENDPOINT } from '@/lib/stream-token';
import { logVideoError } from '@/lib/log-video-error';
import { useCurrentPractitioner } from '@/providers/current-practitioner';

interface VideoContextValue {
  client?: StreamVideoClient;
  isLoading: boolean;
  error?: Error;
}

const VideoContext = createContext<VideoContextValue>({
  isLoading: true,
});

export function useVideoClient() {
  const context = useContext(VideoContext);
  if (!context) {
    throw new Error('useVideoClient must be used within a VideoProvider');
  }
  return context;
}

interface VideoProviderProps {
  children: ReactNode;
}

/**
 * Creates a token provider function for Stream Video authentication.
 */
function createTokenProvider(userId: string) {
  return async (): Promise<string> => {
    const response = await fetch(STREAM_TOKEN_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get video token');
    }

    const { token } = await response.json();
    return token;
  };
}

export function VideoProvider({ children }: VideoProviderProps) {
  const { practitioner } = useCurrentPractitioner();
  const streamUserId = practitioner?.streamUserId;
  const apiKey = process.env.NEXT_PUBLIC_STREAM_API_KEY;

  // Use useMemo to get or create the singleton client instance
  // getOrCreateInstance ensures only one client exists for the same user
  const { client, error } = useMemo(() => {
    if (!streamUserId || !apiKey) {
      return { client: undefined, error: undefined };
    }

    try {
      const user: User = { id: streamUserId };
      const videoClient = StreamVideoClient.getOrCreateInstance({
        apiKey,
        user,
        tokenProvider: createTokenProvider(streamUserId),
      });
      return { client: videoClient, error: undefined };
    } catch (error_) {
      logVideoError('initialize the video client', error_);
      return {
        client: undefined,
        error: error_ instanceof Error ? error_ : new Error('Unknown error'),
      };
    }
  }, [streamUserId, apiKey]);

  // Derive loading state
  const isLoading = Boolean(streamUserId && apiKey && !client && !error);

  // Use StreamVideo context provider from SDK as recommended in docs
  if (!client) {
    return (
      <VideoContext.Provider value={{ client: undefined, isLoading, error }}>
        {children}
      </VideoContext.Provider>
    );
  }

  return (
    <StreamVideo client={client}>
      <VideoContext.Provider value={{ client, isLoading, error }}>
        {children}
      </VideoContext.Provider>
    </StreamVideo>
  );
}
